import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  IconButton,
  useTheme,
  useMediaQuery,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip,
  Grid,
  Alert,
  FormControlLabel,
  Switch
} from '@mui/material';
import {
  Sparkles,
  Plus,
  Edit2,
  Trash2,
  Calendar,
  Package,
  FileText,
  X
} from 'lucide-react';
import { styled } from '@mui/material/styles';
import { motion, AnimatePresence } from 'framer-motion';
import { supplementService } from '../services/agthealthMedicationService';
import AppleSpinner from '../../common/AppleSpinner';
import { format, parseISO } from 'date-fns';
import { es } from 'date-fns/locale';

// Constantes de color para modo wallpaper
const WALLPAPER_COLORS = {
  bg: 'rgba(128, 128, 128, 0.3)',
  bgLight: 'rgba(128, 128, 128, 0.25)',
  border: 'rgba(128, 128, 128, 0.3)',
  text: '#FFFFFF',
  textSecondary: 'rgba(255, 255, 255, 0.7)'
};

// Componentes estilizados
const StyledCard = styled(Card)(({ theme, wallpaperMode }) => ({
  borderRadius: '16px',
  boxShadow: wallpaperMode
    ? 'none'
    : '0 2px 12px rgba(0, 0, 0, 0.08)',
  border: wallpaperMode
    ? `1px solid ${WALLPAPER_COLORS.border}`
    : '1px solid rgba(0, 0, 0, 0.08)',
  backgroundColor: wallpaperMode
    ? WALLPAPER_COLORS.bg
    : '#FFFFFF',
  backdropFilter: wallpaperMode ? 'blur(20px)' : 'none',
  WebkitBackdropFilter: wallpaperMode ? 'blur(20px)' : 'none',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: wallpaperMode
      ? `0 8px 24px ${WALLPAPER_COLORS.border}`
      : '0 8px 24px rgba(0, 0, 0, 0.12)',
  }
}));

const ActionButton = styled(Button)(({ theme, wallpaperMode }) => ({
  borderRadius: '12px',
  textTransform: 'none',
  fontWeight: 600,
  padding: '10px 24px',
  boxShadow: 'none',
  backgroundColor: wallpaperMode ? WALLPAPER_COLORS.bg : theme.palette.primary.main,
  color: wallpaperMode ? WALLPAPER_COLORS.text : '#FFFFFF',
  border: wallpaperMode ? `1px solid ${WALLPAPER_COLORS.border}` : 'none',
  backdropFilter: wallpaperMode ? 'blur(20px)' : 'none',
  WebkitBackdropFilter: wallpaperMode ? 'blur(20px)' : 'none',
  '&:hover': {
    boxShadow: wallpaperMode
      ? `0 4px 12px ${WALLPAPER_COLORS.border}`
      : '0 4px 12px rgba(0, 0, 0, 0.15)',
    backgroundColor: wallpaperMode ? WALLPAPER_COLORS.bgLight : theme.palette.primary.dark,
  }
}));

const Supplements = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const wallpaperMode = theme.palette.actualMode === 'wallpaper';

  const [supplements, setSupplements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingSupplement, setEditingSupplement] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    dosage: '',
    frequency: '',
    startDate: format(new Date(), 'yyyy-MM-dd'),
    endDate: '',
    notes: '',
    brand: ''
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Cargar suplementos
  useEffect(() => {
    loadSupplements();
  }, []);

  const loadSupplements = async () => {
    try {
      setLoading(true);
      const response = await supplementService.getAll();
      if (response.success) {
        setSupplements(response.data);
      }
    } catch (error) {
      console.error('Error al cargar suplementos:', error);
      setError('Error al cargar los suplementos');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (supplement = null) => {
    if (supplement) {
      setEditingSupplement(supplement);
      setFormData({
        name: supplement.name,
        dosage: supplement.dosage,
        frequency: supplement.frequency,
        startDate: supplement.startDate,
        endDate: supplement.endDate || '',
        notes: supplement.notes || '',
        brand: supplement.brand || ''
      });
    } else {
      setEditingSupplement(null);
      setFormData({
        name: '',
        dosage: '',
        frequency: '',
        startDate: format(new Date(), 'yyyy-MM-dd'),
        endDate: '',
        notes: '',
        brand: ''
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingSupplement(null);
    setError('');
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async () => {
    try {
      if (!formData.name || !formData.dosage || !formData.frequency) {
        setError('Por favor completa los campos obligatorios');
        return;
      }

      setLoading(true);
      let response;

      if (editingSupplement) {
        response = await supplementService.update(editingSupplement.supplementId, formData);
      } else {
        response = await supplementService.create(formData);
      }

      if (response.success) {
        setSuccess(editingSupplement ? 'Suplemento actualizado' : 'Suplemento agregado');
        setTimeout(() => setSuccess(''), 3000);
        handleCloseDialog();
        loadSupplements();
      } else {
        setError(response.error || 'Error al guardar el suplemento');
      }
    } catch (error) {
      console.error('Error al guardar suplemento:', error);
      setError('Error al guardar el suplemento');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Estás seguro de eliminar este suplemento?')) {
      return;
    }

    try {
      setLoading(true);
      const response = await supplementService.delete(id);
      if (response.success) {
        setSuccess('Suplemento eliminado');
        setTimeout(() => setSuccess(''), 3000);
        loadSupplements();
      }
    } catch (error) {
      console.error('Error al eliminar suplemento:', error);
      setError('Error al eliminar el suplemento');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleActive = async (id) => {
    try {
      const response = await supplementService.toggleActive(id);
      if (response.success) {
        loadSupplements();
      }
    } catch (error) {
      console.error('Error al cambiar estado:', error);
    }
  };

  if (loading && supplements.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <AppleSpinner sizeDesktop={48} sizeMobile={36} />
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', p: { xs: 2, sm: 3 } }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Sparkles size={isMobile ? 24 : 28} color={wallpaperMode ? WALLPAPER_COLORS.text : theme.palette.primary.main} />
          <Typography
            variant={isMobile ? "h5" : "h4"}
            sx={{
              fontWeight: 700,
              color: wallpaperMode ? WALLPAPER_COLORS.text : 'inherit'
            }}
          >
            Suplementos
          </Typography>
        </Box>
        <ActionButton
          wallpaperMode={wallpaperMode}
          startIcon={<Plus size={20} />}
          onClick={() => handleOpenDialog()}
        >
          {isMobile ? 'Agregar' : 'Agregar Suplemento'}
        </ActionButton>
      </Box>

      {/* Alertas */}
      <AnimatePresence>
        {success && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
          >
            <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>
          </motion.div>
        )}
        {error && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
          >
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Lista de suplementos */}
      <Grid container spacing={2}>
        <AnimatePresence>
          {supplements.map((supplement) => (
            <Grid item xs={12} sm={6} md={4} key={supplement.supplementId}>
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                transition={{ duration: 0.2 }}
              >
                <StyledCard wallpaperMode={wallpaperMode}>
                  <CardContent>
                    {/* Nombre y estado */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 2 }}>
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: 600,
                          color: wallpaperMode ? WALLPAPER_COLORS.text : 'inherit'
                        }}
                      >
                        {supplement.name}
                      </Typography>
                      <Chip
                        label={supplement.active ? 'Activo' : 'Inactivo'}
                        size="small"
                        color={supplement.active ? 'success' : 'default'}
                        sx={{
                          fontWeight: 500,
                          backgroundColor: wallpaperMode && supplement.active
                            ? 'rgba(76, 175, 80, 0.3)'
                            : undefined,
                          color: wallpaperMode ? WALLPAPER_COLORS.text : undefined
                        }}
                      />
                    </Box>

                    {/* Dosificación */}
                    <Typography
                      variant="body1"
                      sx={{
                        mb: 1,
                        color: wallpaperMode ? WALLPAPER_COLORS.text : 'inherit',
                        fontWeight: 500
                      }}
                    >
                      {supplement.dosage}
                    </Typography>

                    {/* Frecuencia */}
                    <Typography
                      variant="body2"
                      sx={{
                        mb: 2,
                        color: wallpaperMode ? WALLPAPER_COLORS.textSecondary : 'text.secondary'
                      }}
                    >
                      {supplement.frequency}
                    </Typography>

                    {/* Detalles adicionales */}
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 2 }}>
                      {supplement.startDate && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Calendar size={14} color={wallpaperMode ? WALLPAPER_COLORS.textSecondary : theme.palette.text.secondary} />
                          <Typography
                            variant="caption"
                            sx={{ color: wallpaperMode ? WALLPAPER_COLORS.textSecondary : 'text.secondary' }}
                          >
                            Desde: {format(parseISO(supplement.startDate), 'dd/MM/yyyy', { locale: es })}
                          </Typography>
                        </Box>
                      )}
                      {supplement.brand && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Package size={14} color={wallpaperMode ? WALLPAPER_COLORS.textSecondary : theme.palette.text.secondary} />
                          <Typography
                            variant="caption"
                            sx={{ color: wallpaperMode ? WALLPAPER_COLORS.textSecondary : 'text.secondary' }}
                          >
                            {supplement.brand}
                          </Typography>
                        </Box>
                      )}
                      {supplement.notes && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <FileText size={14} color={wallpaperMode ? WALLPAPER_COLORS.textSecondary : theme.palette.text.secondary} />
                          <Typography
                            variant="caption"
                            sx={{
                              color: wallpaperMode ? WALLPAPER_COLORS.textSecondary : 'text.secondary',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap'
                            }}
                          >
                            {supplement.notes}
                          </Typography>
                        </Box>
                      )}
                    </Box>

                    {/* Acciones */}
                    <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                      <IconButton
                        size="small"
                        onClick={() => handleToggleActive(supplement.supplementId)}
                        sx={{
                          color: wallpaperMode ? WALLPAPER_COLORS.text : 'inherit',
                          '&:hover': { backgroundColor: wallpaperMode ? WALLPAPER_COLORS.bgLight : undefined }
                        }}
                      >
                        <Switch checked={supplement.active} size="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleOpenDialog(supplement)}
                        sx={{
                          color: wallpaperMode ? WALLPAPER_COLORS.text : 'primary.main',
                          '&:hover': { backgroundColor: wallpaperMode ? WALLPAPER_COLORS.bgLight : undefined }
                        }}
                      >
                        <Edit2 size={18} />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleDelete(supplement.supplementId)}
                        sx={{
                          color: wallpaperMode ? WALLPAPER_COLORS.text : 'error.main',
                          '&:hover': { backgroundColor: wallpaperMode ? WALLPAPER_COLORS.bgLight : undefined }
                        }}
                      >
                        <Trash2 size={18} />
                      </IconButton>
                    </Box>
                  </CardContent>
                </StyledCard>
              </motion.div>
            </Grid>
          ))}
        </AnimatePresence>
      </Grid>

      {/* Mensaje cuando no hay suplementos */}
      {supplements.length === 0 && !loading && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '300px',
            gap: 2
          }}
        >
          <Sparkles size={48} color={wallpaperMode ? WALLPAPER_COLORS.textSecondary : theme.palette.text.secondary} />
          <Typography
            variant="h6"
            sx={{
              color: wallpaperMode ? WALLPAPER_COLORS.textSecondary : 'text.secondary',
              textAlign: 'center'
            }}
          >
            No tienes suplementos registrados
          </Typography>
          <ActionButton wallpaperMode={wallpaperMode} startIcon={<Plus size={20} />} onClick={() => handleOpenDialog()}>
            Agregar tu primer suplemento
          </ActionButton>
        </Box>
      )}

      {/* Dialog para crear/editar */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '16px',
            backgroundColor: wallpaperMode ? WALLPAPER_COLORS.bg : undefined,
            backdropFilter: wallpaperMode ? 'blur(20px)' : 'none',
            WebkitBackdropFilter: wallpaperMode ? 'blur(20px)' : 'none',
            border: wallpaperMode ? `1px solid ${WALLPAPER_COLORS.border}` : undefined,
          }
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            color: wallpaperMode ? WALLPAPER_COLORS.text : undefined
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {editingSupplement ? 'Editar Suplemento' : 'Nuevo Suplemento'}
          </Typography>
          <IconButton onClick={handleCloseDialog} size="small">
            <X size={20} color={wallpaperMode ? WALLPAPER_COLORS.text : undefined} />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <TextField
              label="Nombre del suplemento *"
              name="name"
              value={formData.name}
              onChange={handleChange}
              fullWidth
              InputLabelProps={{
                sx: { color: wallpaperMode ? WALLPAPER_COLORS.textSecondary : undefined }
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  color: wallpaperMode ? WALLPAPER_COLORS.text : undefined,
                  '& fieldset': {
                    borderColor: wallpaperMode ? WALLPAPER_COLORS.border : undefined,
                  },
                }
              }}
            />
            <TextField
              label="Dosificación *"
              name="dosage"
              value={formData.dosage}
              onChange={handleChange}
              fullWidth
              placeholder="ej: 1000 UI"
              InputLabelProps={{
                sx: { color: wallpaperMode ? WALLPAPER_COLORS.textSecondary : undefined }
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  color: wallpaperMode ? WALLPAPER_COLORS.text : undefined,
                  '& fieldset': {
                    borderColor: wallpaperMode ? WALLPAPER_COLORS.border : undefined,
                  },
                }
              }}
            />
            <TextField
              label="Frecuencia *"
              name="frequency"
              value={formData.frequency}
              onChange={handleChange}
              fullWidth
              placeholder="ej: Una vez al día"
              InputLabelProps={{
                sx: { color: wallpaperMode ? WALLPAPER_COLORS.textSecondary : undefined }
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  color: wallpaperMode ? WALLPAPER_COLORS.text : undefined,
                  '& fieldset': {
                    borderColor: wallpaperMode ? WALLPAPER_COLORS.border : undefined,
                  },
                }
              }}
            />
            <TextField
              label="Fecha de inicio"
              name="startDate"
              type="date"
              value={formData.startDate}
              onChange={handleChange}
              fullWidth
              InputLabelProps={{
                shrink: true,
                sx: { color: wallpaperMode ? WALLPAPER_COLORS.textSecondary : undefined }
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  color: wallpaperMode ? WALLPAPER_COLORS.text : undefined,
                  '& fieldset': {
                    borderColor: wallpaperMode ? WALLPAPER_COLORS.border : undefined,
                  },
                }
              }}
            />
            <TextField
              label="Fecha de fin (opcional)"
              name="endDate"
              type="date"
              value={formData.endDate}
              onChange={handleChange}
              fullWidth
              InputLabelProps={{
                shrink: true,
                sx: { color: wallpaperMode ? WALLPAPER_COLORS.textSecondary : undefined }
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  color: wallpaperMode ? WALLPAPER_COLORS.text : undefined,
                  '& fieldset': {
                    borderColor: wallpaperMode ? WALLPAPER_COLORS.border : undefined,
                  },
                }
              }}
            />
            <TextField
              label="Marca"
              name="brand"
              value={formData.brand}
              onChange={handleChange}
              fullWidth
              placeholder="ej: Nature Made"
              InputLabelProps={{
                sx: { color: wallpaperMode ? WALLPAPER_COLORS.textSecondary : undefined }
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  color: wallpaperMode ? WALLPAPER_COLORS.text : undefined,
                  '& fieldset': {
                    borderColor: wallpaperMode ? WALLPAPER_COLORS.border : undefined,
                  },
                }
              }}
            />
            <TextField
              label="Notas"
              name="notes"
              value={formData.notes}
              onChange={handleChange}
              fullWidth
              multiline
              rows={3}
              placeholder="Notas adicionales..."
              InputLabelProps={{
                sx: { color: wallpaperMode ? WALLPAPER_COLORS.textSecondary : undefined }
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  color: wallpaperMode ? WALLPAPER_COLORS.text : undefined,
                  '& fieldset': {
                    borderColor: wallpaperMode ? WALLPAPER_COLORS.border : undefined,
                  },
                }
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3, gap: 1 }}>
          <Button
            onClick={handleCloseDialog}
            sx={{
              borderRadius: '12px',
              textTransform: 'none',
              color: wallpaperMode ? WALLPAPER_COLORS.text : undefined
            }}
          >
            Cancelar
          </Button>
          <ActionButton wallpaperMode={wallpaperMode} onClick={handleSubmit} disabled={loading}>
            {loading ? <AppleSpinner sizeDesktop={20} sizeMobile={16} /> : (editingSupplement ? 'Actualizar' : 'Agregar')}
          </ActionButton>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Supplements;
